import type { FC } from 'react'

import { Button } from '@skytech/ui/Button'
import { ImageWithIconFallback } from '@skytech/ui/Image'
import CheckboxBlankCircleOutlineIcon from 'mdi-react/CheckboxBlankCircleOutlineIcon'
import CheckboxMarkedCircleOutlineIcon from 'mdi-react/CheckboxMarkedCircleOutlineIcon'
import ImageOffOutlineIcon from 'mdi-react/ImageOffOutlineIcon'
import MinusIcon from 'mdi-react/MinusIcon'
import PlusIcon from 'mdi-react/PlusIcon'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import Highlight from '@/components/Highlight'
import { useIsAdmin, useIsThreeStepIT } from '@/hooks/access'
import { parseInStock } from '@/modules/ecom-policy/helpers'
import { useShopNewOrderContext } from '@/modules/ecom-policy/shop/new-order/ShopNewOrder.context'

const Product: FC<{
  id: number
  name: string
  alias: string | null
  imgUrl: string | null
  inStock: string | null
  price: number
  selectedProductId: number | null
  currency: string | null
  quantity: number
  quantityLimit: number
  onSelectProduct: () => void
  onChangeQuantity: (quantity: number) => void
}> = ({
  id,
  name,
  alias,
  imgUrl,
  price,
  inStock,
  currency,
  selectedProductId,
  quantity,
  quantityLimit,
  onChangeQuantity,
  onSelectProduct,
}) => {
  const isThreeStepIT = useIsThreeStepIT()
  const { searchFilter } = useShopNewOrderContext()

  const isSelected = selectedProductId === id
  const isAdmin = useIsAdmin()
  const isQuantitySelectionAllowed = isAdmin || quantityLimit > 1
  const isQuantitySelectionAvailable = isSelected && isQuantitySelectionAllowed
  const isQuantityLimitReached = quantity + 1 > quantityLimit

  return (
    <button
      className={`inventory__products__product ${isSelected ? 'selected' : 'not-selected'}`}
      onClick={onSelectProduct}
    >
      <div className="img">
        <ImageWithIconFallback
          src={imgUrl ?? ''}
          alt="product"
          fallbackIcon={<ImageOffOutlineIcon className="missing-image-placeholder" />}
        />
      </div>

      {isQuantitySelectionAvailable && (
        <div className="absolute right-[11px] top-10 flex flex-col justify-center text-lg">
          <Button
            disabled={isQuantityLimitReached}
            className={twJoin(
              'm-0 p-0',
              !isThreeStepIT &&
                'text-gray-400 hover:enabled:text-green-700 focus:enabled:text-green-700 disabled:text-gray-300'
            )}
            variant="tertiary"
            Icon={<PlusIcon className="m-0" />}
            onClick={e => {
              e.stopPropagation()
              onChangeQuantity(quantity + 1)
            }}
          />
          <div className={twJoin(isThreeStepIT ? 'text-threeStepIT' : 'text-green-700')}>
            {quantity}
          </div>
          <Button
            className={twJoin(
              'm-0 p-0',
              !isThreeStepIT &&
                'text-gray-400 hover:enabled:text-green-700 focus:enabled:text-green-700 disabled:text-gray-300'
            )}
            variant="tertiary"
            Icon={<MinusIcon className="!m-0" />}
            onClick={e => {
              e.stopPropagation()
              if (quantity === 1) {
                onSelectProduct()
                return
              }
              onChangeQuantity(quantity - 1)
            }}
          />
        </div>
      )}

      <div className="name">
        <Highlight pattern={searchFilter} text={alias || name} />
      </div>

      <div className="price">
        <FormattedNumberWithCurrency amount={price} currency={currency} />
      </div>

      <div className="in-stock">
        {!inStock || parseInStock(inStock) === '0' ? (
          <Translation id="Available in stock" />
        ) : (
          <>
            {inStock} <Translation id="in stock" />
          </>
        )}
      </div>

      <div
        className={twJoin(
          'checkmark',
          isSelected ? 'absolute right-2 top-2 block' : 'hidden',
          isSelected && (isThreeStepIT ? 'fill-threeStepIT' : 'fill-green-selected')
        )}
      >
        {isSelected ? (
          <CheckboxMarkedCircleOutlineIcon
            color={twJoin(isThreeStepIT ? 'fill-threeStepIT' : 'fill-green-selected')}
          />
        ) : (
          <CheckboxBlankCircleOutlineIcon />
        )}
      </div>
    </button>
  )
}

export default Product
